import { Component, DestroyRef, inject, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { PaypalService, PaypalLogDTO } from '../../../core/services/register/servicesPaypal/paypal/paypal.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * PAYPAL PRO COMPONENT - Frontend component for Pro subscription
 */
@Component({
    selector: 'app-paypal-pro',
    templateUrl: './paypal-pro.component.html',
    styleUrls: ['./paypal-pro.component.scss'],
    imports: [CommonModule, FormsModule, TranslateModule]
})
export class PaypalProComponent implements OnInit, OnChanges {
  private paypalService = inject(PaypalService);

  @Input() idUser!: number;
  @Input() paymentMode?: 'PRO_MENSUEL' | 'PRO_ANNUEL';

  internalPaymentMode: 'PRO_MENSUEL' | 'PRO_ANNUEL' = 'PRO_ANNUEL';

  loaded = false;
  paypalButtonId = '';
  paypalActionUrl = '';

  private proButtonId = '';
  private proNoTrialButtonId = '';

  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.paypalService.buttonProperties()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (props) => {
          this.proButtonId = props.proButtonId ?? '';
          this.proNoTrialButtonId = props.proNoTrialButtonId ?? '';
          this.paypalActionUrl = props.paypalActionUrl ?? '';
          this.updateButtonId();
          this.loaded = true;
        },
        error: (err) => {
          console.error('[PayPal][Pro] Error fetching button properties:', err);
          this.loaded = true; // Still show the component even if there's an error
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Update button ID when payment mode changes
    if (changes['paymentMode'] && this.loaded) {
      this.updateButtonId();
    }
    // Sync internalPaymentMode with paymentMode input if provided
    if (changes['paymentMode'] && this.paymentMode) {
      this.internalPaymentMode = this.paymentMode;
    }
  }

  private updateButtonId() {
    this.paypalButtonId = this.getButtonId();
  }

  get effectivePaymentMode() {
    return this.paymentMode ?? this.internalPaymentMode;
  }

  private getButtonId(): string {
    // Use no-trial button for annual, regular button for monthly
    // Similar to Premium: use the button that supports both modes if available
    if (this.effectivePaymentMode === 'PRO_ANNUEL' && this.proNoTrialButtonId) {
      return this.proNoTrialButtonId;
    }
    // If no-trial button exists and regular button is empty, use no-trial for both
    if (!this.proButtonId && this.proNoTrialButtonId) {
      return this.proNoTrialButtonId;
    }
    return this.proButtonId;
  }

  /**
   * Get the os0 value for PayPal form submission
   * PayPal buttons now use "Mensuel" or "Annuel" for all environments
   */
  private getOs0Value(): string {
    return this.effectivePaymentMode === 'PRO_ANNUEL' ? 'Annuel' : 'Mensuel';
  }

  subscribe() {
    if (!this.idUser || this.idUser == 0) {
      console.error('[PayPal][Pro] Invalid user ID:', this.idUser);
      alert('Erreur: ID utilisateur invalide.');
      return;
    }
    
    const buttonId = this.getButtonId();
    
    if (!buttonId || buttonId.trim() === '') {
      console.error('[PayPal][Pro] Button ID is empty!');
      alert('Erreur: Le bouton PayPal PRO n\'est pas configuré. Veuillez contacter le support.');
      return;
    }

    if (!this.paypalActionUrl || this.paypalActionUrl.trim() === '') {
      console.error('[PayPal][Pro] PayPal action URL is empty!');
      alert('Erreur: L\'URL PayPal n\'est pas configurée. Veuillez contacter le support.');
      return;
    }
    
    const paymentMode = this.effectivePaymentMode === 'PRO_ANNUEL' ? 'ANNUEL' : 'MENSUEL';
    
    const log: PaypalLogDTO = {
      actionType: 'SUBSCRIPTION',
      idUser: this.idUser,
      paymentMode: paymentMode as any,
      paypalButtonId: buttonId,
      subscriptionType: 'PRO'
    };
    
    this.paypalService.logPublic(log)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitPaypalForm(buttonId);
        },
        error: (err) => {
          console.error('[PayPal][Pro] Error logging subscription:', err);
          // Still submit the form even if logging fails
          this.submitPaypalForm(buttonId);
        }
      });
  }

  private submitPaypalForm(buttonId: string) {
    const form = document.createElement('form');
    form.method = 'post';
    form.target = '_top';
    form.action = this.paypalActionUrl;

    const os0Value = this.getOs0Value();
    const returnUrl = `${window.location.origin}/authentication/login`;

    // Create form fields using createElement to avoid CSP violations (same as Premium)
    const fields = [
      { name: 'cmd', value: '_s-xclick' },
      { name: 'hosted_button_id', value: buttonId },
      { name: 'on0', value: 'Souscription-Budgetfacile-Pro' },
      { name: 'os0', value: os0Value },
      { name: 'currency_code', value: 'EUR' },
      { name: 'custom', value: String(this.idUser) },
      { name: 'return', value: returnUrl },
      { name: 'rm', value: '2' },
      { name: 'cancel_return', value: returnUrl }
    ];

    fields.forEach(field => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = field.name;
      input.value = field.value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }
}
